import React from "react";
import { Bluelagoon, useBluelagoon } from "@bluelagoon";
import Icon from "../components/Icon";
import Message from "../components/Message";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@shadcn-in-nx/ui/alert-dialog";

const FB_ADS_SCOPES = "read_insights,ads_management,ads_read,pages_read_engagement,pages_show_list";

export function FacebookAdsEmbeddedModal({ onClose, onSuccess }) {
    const [step, setStep] = React.useState("intro");
    const [setupData, setSetupData] = React.useState({});
    const [isLoading, setIsLoading] = React.useState(false);
    const [facebookSDKLoaded, setFacebookSDKLoaded] = React.useState(false);
    const [showConfirmClose, setShowConfirmClose] = React.useState(false);

    const { call: createIntegration } = useBluelagoon({
        method: "createIntegration",
    });

    const hasSetupStarted =
        step !== "intro" ||
        isLoading ||
        Object.keys(setupData).length > 0;

    const handleClose = () => {
        if (hasSetupStarted) {
            setShowConfirmClose(true);
        } else {
            onClose();
        }
    };

    React.useEffect(() => {
        const loadFacebookSDK = () => {
            // SDK already loaded and initialized (e.g. by WhatsApp modal)
            if (window.FB && window.FB.login) {
                setFacebookSDKLoaded(true);
                return;
            }

            const prevFbAsyncInit = window.fbAsyncInit;
            window.fbAsyncInit = function () {
                if (window.FB) {
                    try {
                        window.FB.init({
                            appId: Bluelagoon.settings.whatsapp.appId,
                            autoLogAppEvents: true,
                            xfbml: true,
                            version: "v20.0",
                        });
                        setFacebookSDKLoaded(true);
                    } catch (error) {
                        console.error("Error initializing Facebook SDK:", error);
                        setFacebookSDKLoaded(false);
                        toast.error("Failed to initialize Facebook SDK. Please refresh and try again.");
                    }
                }
            };

            if (!document.querySelector('script[src*="connect.facebook.net/en_US/sdk.js"]')) {
                const script = document.createElement("script");
                script.async = true;
                script.defer = true;
                script.crossOrigin = "anonymous";
                script.src = "https://connect.facebook.net/en_US/sdk.js";
                script.onerror = () => {
                    setFacebookSDKLoaded(false);
                    toast.error("Failed to load Facebook SDK.");
                };
                document.head.appendChild(script);
            } else {
                // Script tag exists but SDK not ready yet - poll for it
                const interval = setInterval(() => {
                    if (window.FB && window.FB.login) {
                        setFacebookSDKLoaded(true);
                        clearInterval(interval);
                    }
                }, 200);
                return () => clearInterval(interval);
            }
        };

        return loadFacebookSDK();
    }, []);

    const launchFacebookLogin = () => {
        if (!facebookSDKLoaded || !window.FB || !window.FB.login) {
            toast.error("Facebook SDK is not ready. Please refresh the page.");
            return;
        }

        setIsLoading(true);
        try {
            window.FB.login(
                (response) => {
                    if (response.authResponse) {
                        setSetupData({
                            accessToken: response.authResponse.accessToken,
                        });
                        setStep("review");
                        toast.success("Successfully connected to Facebook!");
                    } else {
                        toast.error("Facebook login was cancelled or failed");
                    }
                    setIsLoading(false);
                },
                { scope: FB_ADS_SCOPES }
            );
        } catch (error) {
            console.error("Error launching Facebook login:", error);
            toast.error("Failed to launch Facebook login. Please try again.");
            setIsLoading(false);
        }
    };

    const handleCreateIntegration = async () => {
        if (!setupData.accessToken) {
            toast.error("Missing access token. Please complete the Facebook login.");
            return;
        }

        setIsLoading(true);
        try {
            await createIntegration({
                platform: "facebook",
                type: "facebook",
                name: "My Facebook Integration",
                config: {
                    name: "My Facebook Integration",
                    accessToken: setupData.accessToken,
                },
            });
            toast.success("Facebook Ads integration created successfully!");
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error("Error creating Facebook Ads integration:", error);
            toast.error("Failed to create Facebook Ads integration");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Dialog open={true} onOpenChange={handleClose}>
                <DialogContent
                    className="w-full max-w-md mx-4 sm:mx-auto max-h-[90vh] overflow-hidden flex flex-col"
                    onPointerDownOutside={(e) => {
                        if (hasSetupStarted) e.preventDefault();
                    }}
                >
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3">
                            <img src="/icons/facebook.png" alt="Facebook" className="w-8 h-8" />
                            Connect Facebook Ads
                        </DialogTitle>
                        <DialogDescription>
                            Connect your Facebook account to manage ad campaigns, insights, and audiences
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto space-y-4 py-2 px-1">
                        {step === "intro" && (
                            <div className="space-y-4">
                                <Message>
                                    <div className="space-y-3">
                                        <h4 className="font-medium">Permissions requested:</h4>
                                        <ul className="list-disc list-inside space-y-1 text-sm">
                                            <li>Read Insights - View ad performance metrics</li>
                                            <li>Ads Management - Create and manage campaigns</li>
                                            <li>Ads Read - Read ad account data</li>
                                            <li>Pages Read Engagement - Read page posts, comments, and engagement</li>
                                        </ul>
                                    </div>
                                </Message>

                                <div className="flex flex-col gap-3">
                                    <button
                                        onClick={launchFacebookLogin}
                                        disabled={!facebookSDKLoaded || isLoading}
                                        className="w-full flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#166FE5] text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isLoading ? (
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                                        ) : (
                                            <Icon name="Facebook" className="w-5 h-5" />
                                        )}
                                        {facebookSDKLoaded ? "Connect with Facebook" : "Loading..."}
                                    </button>
                                    <button
                                        onClick={onClose}
                                        className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}

                        {step === "review" && (
                            <div className="space-y-4">
                                <Message variant="success">
                                    <div className="space-y-2">
                                        <h4 className="font-medium">Facebook Connected!</h4>
                                        <p className="text-sm">
                                            Your Facebook account has been successfully connected.
                                        </p>
                                    </div>
                                </Message>

                                <div className="flex flex-col gap-3">
                                    <button
                                        onClick={handleCreateIntegration}
                                        disabled={isLoading}
                                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {isLoading ? (
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                                        ) : (
                                            <Icon name="Check" className="w-5 h-5" />
                                        )}
                                        Complete Setup
                                    </button>
                                    <button
                                        onClick={() => setStep("intro")}
                                        className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 transition-colors"
                                        disabled={isLoading}
                                    >
                                        Back
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={showConfirmClose} onOpenChange={setShowConfirmClose}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Cancel Facebook Ads Setup?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You're in the middle of setting up your Facebook Ads integration.
                            If you close now, you'll need to start over. Are you sure?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setShowConfirmClose(false)}>
                            Continue Setup
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => { setShowConfirmClose(false); onClose(); }}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            Cancel Setup
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
